// DefectEditorSheet — the item card: description, priority, status, trade,
// assignee, location, photos and the client's question thread.
//
// 2026-10-29 (v0.263.0) — lifted out of PlanScreen, where it was the local
// DefectBottomSheet, so the register opens the same card. Brett: "you need a
// different popup for editing the details/fields ie the same one when we edit
// the pin in plan view." The plan's pin card and the register's ⋮ Edit details
// are now this one component; on the plan nothing changes.
//
// onPinAt / onSwitchPin belong to the plan: a tap outside the card that lands
// on another pin opens that pin. Without them (the register) a tap outside
// just closes, asking first if there are unsaved changes.
//
// items / onOpenItem (v0.287.0) — Previous and Next. Brett: "need a next or
// previus item button". The page passes the items in the order it shows them
// (the register's rows as filtered, the plan's pins on this drawing) and how
// to open one. With unsaved changes a step asks, then saves before it moves —
// stepping never throws work away and never saves without saying so.
//
//
// v0.4.0 (DropPin port) — Simplified from InSpec which had supervisor /
// subcontractor pickers, photo gallery, due-date logic, etc. Here:
//   • Description (textarea)
//   • Priority (low / medium / high)
//   • Status (open / on_hold / rectified / complete / cancelled, v0.312.0)
//   • Assignee (free-text — TODO_DROPPIN: contacts picker later)
//   • Photos (upload skeleton — TODO_DROPPIN: real upload endpoint)
// v0.7.3 - compact centered modal redesign (was full-width bottom-sheet).
//   - 480px wide, auto-height capped at 80vh with internal scroll
//   - dimmed backdrop, click-outside closes
//   - segmented buttons for Priority (4) + Status (3) instead of dropdowns
//   - priority adds 'urgent' (4 levels) to match the register screen
//   - new LocationLabel input
//   - 60x60 photo strip + "Add photo" tile at the end
//   - inline save error (no alerts)
//   - delete (ghost red, left) / cancel + save (right) footer
// v0.7.4 - fix defect modal not loading photos on reopen
//          (modal now fetches /api/defects/:id when opening an existing defect)
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiGet } from '../api';
import { theme, input, btnPrimary, btnSecondary } from '../theme';
import { useActiveTrades } from '../utils/useTrades';
import { Camera, Images, ChevronLeft, ChevronRight } from 'lucide-react';
// v0.36.1 — the shared fullscreen photo viewer.
import PhotoLightbox from './PhotoLightbox';
import { pairRoles } from '../utils/photoPairs';
// v0.328.0 - DropPin's own ghost camera for the fix photo.
import GhostCamera from './GhostCamera';
import { ghostBefore } from '../utils/ghostMath';
// v0.127.0 — conversation thread + reply composer.
import DefectConversation from './DefectConversation';
// v0.279.0 — marks burnt into a copy of a photo for the conversation.
import { burnMarks } from './PhotoMarkupStage';
// v0.171.0 — site mode: photos still on this device show on the card.
import { isLocalId, pendingPhotoRows, pendingPhotoDeleteIds } from '../utils/outbox-local.js';
import { listOutbox, blobToDataUrl } from '../utils/outbox.js';
import { getCurrentIdentityKey } from '../utils/outbox-identity.js';
import { queuedPhotoView } from '../utils/queuedPhotoView.js';
// v0.171.0 — photos are captured as Blobs, never as a base64 string in memory.
import { makePhotoBlobs } from '../utils/imageVariants';
// 2026-09-15 — iPhone photos (HEIC) become JPEG before the card touches them.
import { mayBePhoto, toBrowserImages } from '../utils/heicPhotos';
// v0.312.0 — the status keys, On hold and Cancelled among them.
import { STATUSES, STATUS_LABEL, normaliseStatus } from '../utils/defectStatus';

export default function DefectEditorSheet({ defect, pendingPin, armedTrade, projectContacts, drawings, onSave, onDelete, onCancel, onPinAt, onSwitchPin, items, onOpenItem, focusConversation = false }) {
  // 2026-08-14 — the tenant's own list (Settings -> Mark types), falling back
  // to the built-in trades. A hook so this re-renders when the catalogue loads.
  const TRADES = useActiveTrades();

  const [description, setDescription]     = useState(defect?.description || '');
  const [priority, setPriority]           = useState(defect?.priority || 'medium');
  const [status, setStatus]               = useState(normaliseStatus(defect?.status));
  // v0.45.5 — Type state retained for API back-compat (PATCH payload
  //           still ships `type`) but the UI picker is gone. Defects
  //           always default to 'defect'. RFI/Snag/Observation belong
  //           on the InSpec RFI register, not on DropPin's defect
  //           workflow. Brett: "DropPin never dealt with RFIs."
  const type = defect?.type || 'defect';
  const [assignee, setAssignee]           = useState(defect?.assignee || defect?.assignedTo || '');
  // v0.39.1 — Assignee picker. Sentinels: '__none__' = unassigned,
  //           '__other__' = free-text fallback, anything else = a
  //           Contacts.Id (cast to string for <select value>).
  const [assignedContactId, setAssignedContactId] = useState(() => {
    if (defect?.assignedContactId) return String(defect.assignedContactId);
    if (defect?.assignedTo || defect?.assignee) return '__other__';
    return '__none__';
  });
  const [locationLabel, setLocationLabel] = useState(defect?.locationLabel || '');
  // v0.45.7 — AI ✨ Location autofill on the defect modal.
  // v0.45.11 — Brett: "still no wizard for location on the popup."
  //            Was rendering as a faded greyed-out 16px button hidden
  //            in the corner of the input — easy to miss. Now: full
  //            brand-orange chip, always clickable (uses whatever
  //            context is available — description, drawing, or
  //            location coords).
  const [aiLocationBusy, setAiLocationBusy] = useState(false);
  const suggestLocationAi = async () => {
    if (aiLocationBusy) return;
    // Build the same aiContext shape RegisterScreen ships.
    const pickedDrawing = (drawings || []).find(dr =>
      Number(dr.Id || dr.id) === Number(pendingPin?.drawingId || defect?.drawingId)
    );
    const aiContext = {
      description: (description || '').trim(),
      drawingTitle:  pickedDrawing?.Title || pickedDrawing?.title || pickedDrawing?.Name || pickedDrawing?.name || '',
      drawingNumber: pickedDrawing?.DrawingNumber || pickedDrawing?.drawingNumber || '',
      discipline:    pickedDrawing?.Discipline || pickedDrawing?.discipline || '',
    };
    setAiLocationBusy(true);
    try {
      const r = await fetch('/api/ai-suggest-location', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(aiContext),
      });
      const j = await r.json().catch(() => ({}));
      if (j?.ok && j.suggestion) {
        setLocationLabel(j.suggestion);
      }
    } catch (_) { /* silent — best effort */ }
    finally { setAiLocationBusy(false); }
  };
  const [photos, setPhotos]               = useState(defect?.photos || []);
  const [removedPhotoIds, setRemovedPhotoIds] = useState([]); // v0.69.0 — server photo ids removed in this edit; deleted on save
  const removedPhotoIdsRef = useRef(removedPhotoIds);          // read by the photo load, which lands later
  removedPhotoIdsRef.current = removedPhotoIds;
  const [photoDrop, setPhotoDrop]         = useState(false); // v0.66 drag-drop hover
  const [converting, setConverting]       = useState(0);     // 2026-09-15 photos being made browser-readable
  // v0.36.1 — loading flag for the photo-hydration GET below. We only flip
  // it on when we ACTUALLY need to fetch (existing defect with no inline
  // photos) so the strip doesn't flicker a spinner when photos were
  // prefetched in the list response.
  const [loadingPhotos, setLoadingPhotos] = useState(
    !!defect?.id && !isLocalId(defect?.id) && (!Array.isArray(defect?.photos) || defect.photos.length === 0)
  );
  // v0.171.0 — object URLs minted for previews of photos held on this phone;
  // revoked when the sheet goes away so a long site session does not leak.
  const objectUrlsRef = useRef([]);
  useEffect(() => () => {
    for (const u of objectUrlsRef.current) { try { URL.revokeObjectURL(u); } catch (_e) { /* gone */ } }
    objectUrlsRef.current = [];
  }, []);
  // v0.36.1 — lightbox state: null = closed, integer = open at that index.
  const [lightboxIdx, setLightboxIdx]     = useState(null);
  // v0.317.0 - a fix photo from InSpec's ghost camera and the photo it answers.
  const photoRoles = pairRoles(photos);
  // v0.328.0 - an after taken here and not yet saved is badged After too.
  const roleOf = (p) => {
    if (!p) return undefined;
    if (p.pending && p.kind === 'after') return 'after';
    if (p.id != null && photos.some(q => q.pending && q.kind === 'after' && String(q.beforePhotoId) === String(p.id))) return 'before';
    return photoRoles.get(String(p.id));
  };
  // v0.328.0 - the ghost camera: picking Rectified opens it over the item's
  // first saved photo (the one it was raised with); the shot is saved as that
  // photo's after. No saved photo = no ghost, the ordinary photo buttons.
  const ghost = ghostBefore(photos);
  const [ghostOpen, setGhostOpen]         = useState(false);
  const onStatusChange = (next) => {
    setStatus(next);
    if (next === 'rectified' && next !== status && ghost && !photos.some(p => p.pending && p.kind === 'after')) setGhostOpen(true);
  };
  const onGhostShot = (file) => {
    setGhostOpen(false);
    if (!file || !ghost) return;
    let previewUrl = null;
    try { previewUrl = URL.createObjectURL(file); objectUrlsRef.current.push(previewUrl); }
    catch (err) { console.warn('[DropPin] photo preview failed:', err.message); }
    setPhotos(prev => [...prev, { name: 'After photo', file, url: previewUrl, previewUrl, prepared: makePhotoBlobs(file), pending: true, kind: 'after', beforePhotoId: ghost.id }]);
  };
  // v0.9.0 — trade catalogue: drives the pin colour on the canvas. Default
  // 'general' for existing defects that pre-date the column.
  // v0.9.1 — when armedTrade is set in the parent legend, NEW defects
  // (no defect.id) seed their trade from it. Existing defects keep their
  // stored trade so editing doesn't surprise-recolour them.
  const [trade, setTrade]                 = useState(
    defect?.trade || (defect?.id ? 'general' : (armedTrade || 'general'))
  );
  const [saving, setSaving]               = useState(false);
  const [errMsg, setErrMsg]               = useState('');
  const fileInputRef = useRef(null);
  // 2026-09-15 — the camera, straight away (capture); fileInputRef is the library.
  const cameraInputRef = useRef(null);

  // v0.122.0 — per-item conversation thread + reply composer.
  // v0.127.0 — EXTRACTED to the shared <DefectConversation> component
  // (src/components/DefectConversation.jsx) so RegisterScreen's read-only
  // sheet renders the identical thread for unpinned items. All state,
  // fetch, and send logic now live there; rendered below the photo strip.

  // v0.7.4 — the defects-list response (/api/defects?projectId=N) only
  // returns photoCount + previewPhotoUrl, not the full photos array. When
  // the modal opens for an EXISTING defect (one with a real id), fetch
  // GET /api/defects/:id to hydrate the photo strip. A freshly-created
  // pin has no id yet, so we skip the fetch in that case.
  //
  // v0.171.0 — plus the photos QUEUED ON THIS PHONE for the pin, which the
  // server has not seen yet, rendered from their own bytes. A pin that only
  // exists in the queue ('local:…') skips the server entirely.
  useEffect(() => {
    if (!defect?.id) return;            // new pin — nothing to hydrate
    let cancelled = false;
    (async () => {
      let serverPhotos = null;
      if (!isLocalId(defect.id)) {
        try {
          const full = await apiGet('/api/defects/' + defect.id);
          if (cancelled) return;
          if (Array.isArray(full?.photos)) serverPhotos = full.photos;
        } catch (e) {
          // Non-fatal — leave whatever's already in state. No toast.
          console.warn('[DropPin] could not hydrate defect photos:', e?.message || e);
        }
      }
      let queued = [];
      let deleting = new Set();
      try {
        const rows = await listOutbox();
        // v0.255.0 — one photo's bytes at a time.
        for (const r of pendingPhotoRows(rows, defect.id, getCurrentIdentityKey())) {
          if (cancelled) return;
          queued.push(await queuedPhotoView(r, objectUrlsRef.current));
        }
        deleting = pendingPhotoDeleteIds(rows, getCurrentIdentityKey());
      } catch (_e) { /* nothing readable in the queue */ }
      if (cancelled) return;
      setPhotos(prev => {
        // Anything picked in this sitting (has a File) stays; queued rows are
        // re-read fresh; the server's list replaces the old server list.
        // 2026-09-15 — minus photos already removed: one with a delete waiting
        // in the queue, or one taken off in this sitting before the list landed.
        // Showing them again is what made a removal look like it needed doing
        // twice.
        const gone = (p) => p && p.id != null
          && (deleting.has(String(p.id)) || removedPhotoIdsRef.current.includes(p.id));
        const fresh = prev.filter(p => p && p.file);
        const base = (serverPhotos || prev.filter(p => p && !p.file && !(p.id != null && isLocalId(p.id))))
          .filter(p => !gone(p));
        return [...base, ...queued, ...fresh];
      });
      // v0.36.1 — always clear the spinner, even on hydration failure.
      setLoadingPhotos(false);
    })();
    return () => { cancelled = true; };
  }, [defect?.id]);

  // v0.7.3 — segmented control options.
  const PRIORITY_OPTS = [
    { value: 'low',    label: 'Low',    swatch: theme.textLight },
    { value: 'medium', label: 'Medium', swatch: theme.brand },
    { value: 'high',   label: 'High',   swatch: theme.warn },
    { value: 'urgent', label: 'Urgent', swatch: theme.danger },
  ];
  // v0.45.5 — TYPE_OPTS removed. The Type picker UI was part of the
  //           multi-Type register (defect/rfi/snag/observation) which
  //           Brett confirmed belongs on InSpec's RFI system, not
  //           DropPin's defect workflow.
  // v0.14.0 - 3-state status options. "In progress" dropped (legacy
  // values still load + display fine — they just normalise to Open).
  // v0.312.0 - On hold and Cancelled (Brett: "we need on hold and cancelled").
  const STATUS_OPTS = STATUSES.map(value => ({ value, label: STATUS_LABEL[value] }));

  // 2026-09-15 — WHAT WOULD BE LOST. A tap outside the card, or on another pin,
  // closes it; with unsaved changes it asks first. The first render's values are
  // the card as it opened.
  const openedAs = useRef(null);
  if (openedAs.current === null) openedAs.current = { description, priority, status, assignee, assignedContactId, locationLabel, trade };
  // v0.272.0 — a comment typed in the conversation and not yet sent. Brett:
  // "i tyed a comment and pushed save and it didnt save?" Save posts it now,
  // and closing asks before throwing the words away.
  const composerRef = useRef(null);
  const commentDraft = () => !!(composerRef.current && composerRef.current.draft());
  const hasUnsavedChanges = () => {
    const a = openedAs.current;
    return description !== a.description || priority !== a.priority || status !== a.status
      || assignee !== a.assignee || assignedContactId !== a.assignedContactId
      || locationLabel !== a.locationLabel || trade !== a.trade
      || photos.some(ph => ph && ph.file) || removedPhotoIds.length > 0
      || commentDraft();
  };
  // v0.263.0 — the register opens this card for items with no pin too.
  const noun = pendingPin || defect?.drawingId ? 'pin' : 'item';
  const discardWhat = () => (commentDraft() ? `the changes and the unsent comment on this ${noun}` : `the changes on this ${noun}`);
  const onBackdropTap = (e) => {
    const pin = typeof onPinAt === 'function' ? onPinAt(e.clientX, e.clientY) : null;
    if (pin && defect && String(pin.id) === String(defect.id)) return;   // this card's own pin
    if (hasUnsavedChanges() && !window.confirm(pin
      ? `Discard ${discardWhat()} and open the other one?`
      : `Discard ${discardWhat()}?`)) return;
    if (pin && typeof onSwitchPin === 'function') onSwitchPin(pin);
    else onCancel();
  };
  // 2026-09-15 — Close and Cancel ask too. Only a tap outside the card checked
  // for unsaved changes; the × and Cancel threw them away without a word, so a
  // photo taken off and the card then closed was back next time — a removal
  // that looked like it needed doing twice.
  const requestClose = () => {
    if (hasUnsavedChanges() && !window.confirm(`Discard ${discardWhat()}?`)) return;
    onCancel();
  };

  // v0.272.0 — Brett: "lets split this screen so coversation is on the
  // right." On a screen wide enough for both, an existing item's card shows
  // its form on the left and its conversation down the right; narrower, the
  // conversation stays under the form as before.
  const [wide, setWide] = useState(() => {
    try { return window.matchMedia('(min-width: 1000px)').matches; } catch (_) { return false; }
  });
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia('(min-width: 1000px)'); } catch (_) { return undefined; }
    const on = (e) => setWide(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, []);
  const split = wide && defect?.id != null;
  // v0.334.0 — the card no longer puts the cursor in What's wrong on open: on
  // a phone that raised the keyboard and (under 16px) zoomed the page, leaving
  // the X, Urgent and Save off the edge. Only a NEW pin with a mouse starts
  // typing straight away; an existing item opens to read.
  const [autoFocusWords] = useState(() => {
    if (defect) return false;
    try { return window.matchMedia('(pointer: fine)').matches; } catch (_) { return false; }
  });
  // v0.274.0 — Brett: "the photo viewer should sit over the defect side so
  // comms side stays open." Split, a photo opens over the form only.
  const itemFormRef = useRef(null);
  const overItemForm = useCallback(() => {
    const el = itemFormRef.current;
    return el ? el.getBoundingClientRect() : null;
  }, []);
  // A client's reply photo added to the item lands in the strip at once.
  // Safe on Save: only photos with a file and no id are uploaded.
  const onAddedToItem = (ph) => setPhotos(prev => (prev.some(p => p && p.id === ph.id) ? prev : [...prev, ph]));
  // v0.279.0 — Brett: "dont forget the ability to markup and post it to
  // conversation Just 3 tools, text, arrow and pen." From the photo viewer a
  // photo of a saved item is marked up and posted as the office's message:
  // the marks are burnt into a copy at full size, the item's photo is never
  // changed, and the copy joins the item only by Add to item photos. A photo
  // still on this device (not synced) or a pin the server has not seen yet
  // cannot be posted; the viewer says why instead of offering it.
  const photoMarkup = defect?.id != null ? {
    whyNot: (p) => {
      if (isLocalId(defect.id)) return 'This item has not synced yet — post a marked-up photo once it has.';
      if (!p || p.pending || p.file || p.id == null || isLocalId(p.id)) return 'This photo has not synced yet — post it once it has.';
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'Posting a marked-up photo needs a connection.';
      return null;
    },
    onPost: async (p, marks) => {
      const composer = composerRef.current;
      if (!composer || !composer.postPhoto) return { ok: false, error: 'The conversation is not open — the marked-up photo was not posted.' };
      let burnt;
      try {
        burnt = await burnMarks(p.url || p.thumbnailUrl, marks);
      } catch (e) {
        if (!p.thumbnailUrl || p.thumbnailUrl === p.url) throw e;
        burnt = await burnMarks(p.thumbnailUrl, marks); // the small copy, if the full file will not read
      }
      const v = await makePhotoBlobs(new File([burnt.blob], 'Marked up.jpg', { type: 'image/jpeg' }));
      const [photoUrl, photoDisplayUrl, photoThumbnailUrl] = await Promise.all(
        [v.original, v.display, v.thumbnail].map(b => (b ? blobToDataUrl(b) : null)),
      );
      return composer.postPhoto({ photoUrl, photoDisplayUrl, photoThumbnailUrl });
    },
  } : null;

  // Resolves true once saved, false when it was not (the reason is on the card,
  // or in the page's own alert) — Previous / Next move only on true.
  const submit = async () => {
    if (!description.trim()) {
      setErrMsg('Please add a description.');
      return false;
    }
    setErrMsg('');
    // The typed comment goes first: if it cannot post, nothing is saved and
    // the words stay in the box with the reason under them.
    if (commentDraft()) {
      setSaving(true);
      const posted = await composerRef.current.post();
      if (!posted) {
        setSaving(false);
        setErrMsg('Your comment did not post, so nothing was saved. The reason is under the comment.');
        return false;
      }
    }
    setSaving(true);
    try {
      const saved = await onSave({
        description: description.trim(),
        priority,
        status,
        type, // v0.45.1
        assignee: assignee.trim() || null,
        // v0.39.1 — only ship a real ContactId if the user picked one
        // from the dropdown. '__other__' / '__none__' → null.
        assignedContactId: (assignedContactId !== '__none__' && assignedContactId !== '__other__')
          ? parseInt(assignedContactId, 10)
          : null,
        locationLabel: locationLabel.trim() || null,
        trade,
        photos,
        removedPhotoIds, // v0.69.0 — tell onSheetSave which server photos to delete
      });
      // The plan's save says why in its own alert and answers false.
      return saved !== false;
    } catch (e) {
      // v0.7.3 — inline error rendering instead of alert().
      setErrMsg(e?.message || 'Save failed.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // v0.287.0 — Previous / Next, in the page's own order. The ends do not wrap,
  // so "3 of 12" always means what it says.
  const stepList = Array.isArray(items) && typeof onOpenItem === 'function' ? items : null;
  const stepIdx = stepList && defect?.id != null
    ? stepList.findIndex(d => d && String(d.id) === String(defect.id))
    : -1;
  const canStep = stepIdx >= 0 && stepList.length > 1;
  const prevItem = canStep && stepIdx > 0 ? stepList[stepIdx - 1] : null;
  const nextItem = canStep && stepIdx < stepList.length - 1 ? stepList[stepIdx + 1] : null;
  // Named by the list, not this one: the register mixes pins and loose items,
  // so its buttons say "item" even on a pinned row.
  const stepNoun = canStep && stepList.every(d => d && d.drawingId) ? 'pin' : 'item';
  const stepTo = async (target, which) => {
    if (!target || saving) return;
    if (hasUnsavedChanges()) {
      if (!window.confirm(`Save ${discardWhat()} and open the ${which} one?`)) return;
      if (!(await submit())) return;
    }
    onOpenItem(target);
  };

  const pickPhotos = () => fileInputRef.current?.click();
  const takePhoto = () => cameraInputRef.current?.click();

  const ingestPhotoFiles = async (fileList) => {
    // 2026-09-15 — Brett: "Drag and drop isnt working into droppin". The photos
    // were iPhone HEICs: Chrome cannot show one, so each was a broken tile and
    // went up as HEIC. They are converted to JPEG first (utils/heicPhotos). A
    // Windows drag often carries no type, so a typeless or HEIC-named file counts.
    const incoming = Array.from(fileList || []).filter(f => f && mayBePhoto(f));
    if (!incoming.length) return;
    setConverting(n => n + incoming.length);
    let result;
    try { result = await toBrowserImages(incoming); }
    finally { setConverting(n => Math.max(0, n - incoming.length)); }
    const { files, failed } = result;
    if (failed.length) setErrMsg(`${failed.join(', ')} could not be read. Save ${failed.length === 1 ? 'it' : 'them'} as a JPEG and add ${failed.length === 1 ? 'it' : 'them'} again.`);
    if (!files.length) return;
    // v0.171.0 — no base64. Keep the File, preview it through an object URL,
    // and start the downscale NOW so Save has nothing left to wait on. The
    // bytes go to the outbox as Blobs on save (onSheetSave → queuePhoto).
    const added = [];
    for (const f of files) {
      let previewUrl = null;
      try { previewUrl = URL.createObjectURL(f); objectUrlsRef.current.push(previewUrl); }
      catch (err) { console.warn('[DropPin] photo preview failed:', err.message); }
      added.push({ name: f.name, file: f, url: previewUrl, previewUrl, prepared: makePhotoBlobs(f), pending: true });
    }
    // Added onto the photos as they are NOW — the conversion awaited, and the
    // server's list may have landed meanwhile.
    setPhotos(prev => [...prev, ...added]);
  };
  const onPhotoFiles = async (e) => { await ingestPhotoFiles(e.target.files); e.target.value = ''; };

  // v0.69.0 — when removing a photo that already exists server-side (has an id),
  //   remember its id so submit() can tell the server to delete it on save.
  // 2026-09-15 — removes THAT photo, not whatever sits at its index by the time
  //   the tap lands (the list is rebuilt when the server's photos arrive).
  const removePhoto = (gone) => {
    if (!gone) return;
    if (gone.id != null) setRemovedPhotoIds(ids => ids.includes(gone.id) ? ids : [...ids, gone.id]);
    setPhotos(prev => prev.filter(p => p !== gone && !(gone.id != null && p && p.id === gone.id)));
  };

  const defectNumber =
    defect?.defectNumber != null ? defect.defectNumber :
    defect?.id != null            ? defect.id           : null;

  return (
    <>
      {/* The loading tile spins on fdpSpin; the plan page defines it too, the
          register does not, so the card carries its own. */}
      <style>{'@keyframes fdpSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }'}</style>
      {/* Backdrop — v0.7.3 dimmed, click-outside closes. */}
      <div
        onClick={onBackdropTap}
        style={{
          position: 'fixed', inset: 0, zIndex: 1700,
          background: 'rgba(15,23,42,0.5)',
        }}
      />
      {/* Modal — v0.7.3 centered (was bottom-anchored full-width sheet). */}
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          zIndex: 1701,
          // 2026-09-15 — Brett: "make this card bigger". Was 480 wide, 80 % high.
          ...(split
            ? { width: 1120, maxWidth: '96vw', height: 'min(92vh, 900px)' }
            : { width: 720, maxWidth: '96vw', maxHeight: '92vh' }),
          background: theme.surface,
          borderRadius: 12,
          boxShadow: '0 24px 60px rgba(0,0,0,0.28)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 10,
          padding: '12px 16px',
          borderBottom: `1px solid ${theme.border}`,
        }}>
          <h2 style={{ fontSize: 15, fontWeight: 800, margin: 0, color: theme.text, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {defect
              ? `Defect ${defectNumber != null ? `#${defectNumber}` : ''}`.trim()
              : 'New defect'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {canStep && (
              <div data-part="item-stepper" style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 4 }}>
                <StepButton
                  onClick={() => stepTo(prevItem, 'previous')}
                  disabled={!prevItem || saving}
                  label={`Previous ${stepNoun}`}
                  why={prevItem ? null : `This is the first ${stepNoun}`}
                >
                  <ChevronLeft size={14} aria-hidden /><span className="ss-step-word">Previous</span>
                </StepButton>
                <span style={{ fontSize: 12, color: theme.textLight, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {stepIdx + 1} of {stepList.length}
                </span>
                <StepButton
                  onClick={() => stepTo(nextItem, 'next')}
                  disabled={!nextItem || saving}
                  label={`Next ${stepNoun}`}
                  why={nextItem ? null : `This is the last ${stepNoun}`}
                >
                  <span className="ss-step-word">Next</span><ChevronRight size={14} aria-hidden />
                </StepButton>
              </div>
            )}
            <button
              type="button"
              onClick={requestClose}
              aria-label="Close"
              style={{
                width: 28, height: 28, borderRadius: 6,
                border: 0, background: 'transparent',
                color: theme.textLight, cursor: 'pointer',
                fontSize: 18, lineHeight: 1, padding: 0,
              }}
            >×</button>
          </div>
        </div>

        {/* Body (scrolls if too tall). Split: the form scrolls on the left,
            the conversation holds the right. */}
        <div style={split
          ? { display: 'flex', flex: 1, minHeight: 0 }
          : { padding: '14px 16px', overflowY: 'auto', flex: 1 }}>
        <div data-part="item-form" ref={itemFormRef} style={split
          ? { flex: 1, minWidth: 0, overflowY: 'auto', padding: '14px 16px' }
          : { display: 'contents' }}>
          {pendingPin && !defect && (
            <div style={{
              fontSize: 11, color: theme.textLight, marginBottom: 10,
            }}>
              Pin at {(pendingPin.markX * 100).toFixed(1)}%, {(pendingPin.markY * 100).toFixed(1)}%
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            <Label>Description</Label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              style={{
                ...input,
                minHeight: 66,
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
              placeholder="What's wrong? (e.g. tile chipped, paint touch-up needed)"
              autoFocus={autoFocusWords}
            />
          </div>

          {/* Priority — segmented (low / medium / high / urgent) */}
          <div style={{ marginBottom: 12 }}>
            <Label>Priority</Label>
            <Segmented
              options={PRIORITY_OPTS}
              value={priority}
              onChange={setPriority}
              withSwatch
            />
          </div>

          {/* Status — segmented, the five keys (v0.312.0) */}
          <div style={{ marginBottom: 12 }}>
            <Label>Status</Label>
            <Segmented
              options={STATUS_OPTS}
              value={status}
              onChange={onStatusChange}
            />
          </div>

          {/* v0.45.5 — Type picker, Pinned-to list, and Add-another-pin
              button all removed. RFI workflow (Type=rfi/snag/observation
              + multi-pin) belongs on InSpec's RFI register, not on
              DropPin's defect system. Schema fields stay (Defects.Type
              defaults to 'defect', DefectPins table dormant) so a
              future InSpec integration can light them up without a
              new migration. */}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div>
              <Label>Assignee</Label>
              {/* v0.39.1 — Picker pulls from the project's Contacts
                  list (set up at /contacts → Project edit). Free-text
                  fallback stays for backward compat with un-migrated
                  defects + ad-hoc names. */}
              <select
                value={assignedContactId}
                onChange={(e) => {
                  const v = e.target.value;
                  setAssignedContactId(v);
                  if (v === '__none__') setAssignee('');
                  else if (v === '__other__') { /* keep current text */ }
                  else {
                    const c = projectContacts.find(x => String(x.contactId) === v);
                    setAssignee(c?.name || '');
                  }
                }}
                style={input}
              >
                <option value="__none__">— Unassigned —</option>
                {projectContacts.map(c => (
                  <option key={c.contactId} value={String(c.contactId)}>
                    {c.name}{c.trade ? ` · ${c.trade}` : ''}
                  </option>
                ))}
                <option value="__other__">— Other (type a name) —</option>
              </select>
              {assignedContactId === '__other__' && (
                <input
                  value={assignee}
                  onChange={(e) => setAssignee(e.target.value)}
                  style={{ ...input, marginTop: 6 }}
                  placeholder="Type a name"
                />
              )}
            </div>
            <div>
              <Label>Location</Label>
              {/* v0.45.11 — AI ✨ Location wizard, now a prominent
                  brand-orange chip beside the input (was a faint
                  corner sparkle, easy to miss). Always clickable;
                  the helper will fall back to drawing metadata if
                  description is empty. */}
              <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                <input
                  value={locationLabel}
                  onChange={(e) => setLocationLabel(e.target.value)}
                  style={{ ...input, flex: 1 }}
                  placeholder="e.g. Level 2, Unit 4"
                  disabled={aiLocationBusy}
                />
                <button
                  type="button"
                  onClick={suggestLocationAi}
                  disabled={aiLocationBusy}
                  title="Suggest a location with AI"
                  style={{
                    flexShrink: 0,
                    background: theme.brand,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    padding: '0 12px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: aiLocationBusy ? 'wait' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    whiteSpace: 'nowrap',
                  }}
                  aria-label="Suggest location with AI"
                >{aiLocationBusy ? '…' : '✨ Suggest'}</button>
              </div>
            </div>
          </div>

          {/* Trade — v0.9.0 — colour-swatch row. Tap a swatch to set the
              trade key; the pin on the canvas + the report PDF + the
              register thumbnail outline all read this. */}
          <div style={{ marginBottom: 12 }}>
            <Label>Trade</Label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {TRADES.map(t => {
                const active = trade === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTrade(t.key)}
                    title={t.label}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                      padding: '4px 6px',
                      borderRadius: 6,
                      border: active ? `2px solid ${t.color}` : `1px solid ${theme.border}`,
                      background: active ? `${t.color}22` : 'transparent',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      minWidth: 56,
                    }}
                    aria-pressed={active}
                  >
                    <span style={{
                      width: 22, height: 22, borderRadius: '50%',
                      background: t.color,
                      border: active ? '2px solid #fff' : '1px solid rgba(0,0,0,0.1)',
                      boxShadow: active ? `0 0 0 1px ${t.color}` : 'none',
                      display: 'block',
                    }} />
                    <span style={{ fontSize: 10, fontWeight: active ? 700 : 500, color: theme.text }}>
                      {t.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Photos — compact 60x60 strip with "Add" tile on the right. */}
          {/* v0.36.1 — Spinner placeholder while we hydrate photos for an
                       existing defect (the list endpoint only returns
                       photoCount, so there's a noticeable gap on open
                       for defects with many photos). Click-to-enlarge via
                       the shared PhotoLightbox at the bottom of this file. */}
          <div style={{ marginBottom: 8 }}>
            <Label>Photos</Label>
            {status === 'rectified' && ghost && (
              <div style={{ margin: '0 0 8px' }}>
                <button type="button" onClick={() => setGhostOpen(true)} style={btnSecondary}>
                  Take the after photo
                </button>
                <div style={{ fontSize: 11, color: theme.textLight, marginTop: 4 }}>
                  The original photo is faded over the camera, so the fix is taken from the same spot.
                </div>
              </div>
            )}
            {ghostOpen && ghost && (
              <GhostCamera beforePhoto={ghost} onUse={onGhostShot} onClose={() => setGhostOpen(false)} />
            )}
            {converting > 0 && (
              <div style={{ fontSize: 11, color: theme.textLight, margin: '0 0 6px' }}>
                Preparing {converting} photo{converting === 1 ? '' : 's'}…
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={onPhotoFiles}
              style={{ display: 'none' }}
            />
            {/* 2026-09-15 — Brett: "i dont want to be asked every time is it a
                photo or from the library". Take photo opens the camera straight
                away; Choose photos opens the library. */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onPhotoFiles}
              style={{ display: 'none' }}
            />
            <div
              onDragOver={(e) => { e.preventDefault(); setPhotoDrop(true); }}
              onDragLeave={(e) => { e.preventDefault(); setPhotoDrop(false); }}
              onDrop={(e) => { e.preventDefault(); setPhotoDrop(false); ingestPhotoFiles(e.dataTransfer && e.dataTransfer.files); }}
              title="Drag images here from your computer, or tap Choose photos"
              style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: 8, borderRadius: 8, border: '2px dashed ' + (photoDrop ? theme.brand : theme.border), background: photoDrop ? theme.bg : 'transparent', transition: 'border-color 0.12s, background 0.12s' }}>
              {loadingPhotos && photos.length === 0 && (
                <div
                  aria-label="Loading photos"
                  style={{
                    width: 88, height: 88, borderRadius: 6,
                    border: `1px solid ${theme.border}`,
                    background: theme.bg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: theme.textLight, fontSize: 10, fontWeight: 600,
                    flexDirection: 'column', gap: 4,
                  }}
                >
                  <span style={{
                    width: 16, height: 16, borderRadius: '50%',
                    border: `2px solid ${theme.border}`,
                    borderTopColor: theme.brand,
                    animation: 'fdpSpin 0.8s linear infinite',
                  }} />
                  <span style={{ fontSize: 9 }}>loading</span>
                </div>
              )}
              {photos.map((p, i) => (
                <div
                  key={p.id != null ? `photo-${p.id}` : `new-${i}`}
                  onClick={() => setLightboxIdx(i)}
                  title="Click to enlarge"
                  style={{
                    position: 'relative',
                    width: 88, height: 88,
                    borderRadius: 6, overflow: 'hidden',
                    border: `1px solid ${theme.border}`,
                    background: theme.bg,
                    cursor: 'zoom-in',
                  }}
                >
                  <img
                    loading="lazy"
                    src={p.thumbnailUrl || p.dataUrl || p.previewUrl || p.url}
                    alt={p.name || p.caption || `photo ${i + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  {roleOf(p) && (
                    <span
                      data-role={roleOf(p)}
                      style={{
                        position: 'absolute', top: 3, left: 3, padding: '1px 5px', borderRadius: 3,
                        fontSize: 9, fontWeight: 700, letterSpacing: 0.3,
                        background: roleOf(p) === 'after' ? theme.success : theme.text,
                        color: theme.surface,
                      }}
                    >{roleOf(p) === 'after' ? 'After' : 'Before'}</span>
                  )}
                  {/* v0.171.0 — held on this phone until the next sync. */}
                  {p.pending && (
                    <span
                      title="Saved on this phone — not synced yet"
                      style={{
                        position: 'absolute', left: 0, right: 0, bottom: 0,
                        fontSize: 8, fontWeight: 700, letterSpacing: 0.3, textAlign: 'center',
                        padding: '1px 0', background: theme.warn, color: '#fff',
                        textTransform: 'uppercase',
                      }}
                    >on phone</span>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removePhoto(p); }}
                    aria-label="Remove photo"
                    style={{
                      position: 'absolute', top: 3, right: 3,
                      width: 28, height: 28, borderRadius: '50%',
                      border: 0, background: 'rgba(0,0,0,0.6)', color: 'white',
                      fontSize: 13, lineHeight: 1, cursor: 'pointer',
                      padding: 0,
                    }}
                    title="Remove"
                  >×</button>
                </div>
              ))}
              {[
                { key: 'camera', onClick: takePhoto, title: 'Take a photo with the camera', label: 'Take photo', Icon: Camera },
                { key: 'library', onClick: pickPhotos, title: 'Choose photos from the library', label: 'Choose photos', Icon: Images },
              ].map(({ key, onClick, title, label, Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={onClick}
                  title={title}
                  style={{
                    width: 88, height: 88,
                    borderRadius: 6,
                    border: `1px dashed ${theme.border}`,
                    background: theme.bg,
                    color: theme.textLight,
                    cursor: 'pointer',
                    fontSize: 11, fontWeight: 600, lineHeight: 1.15,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexDirection: 'column', gap: 3, padding: 2, textAlign: 'center',
                    fontFamily: 'inherit',
                  }}
                >
                  <Icon size={22} strokeWidth={2} aria-hidden />
                  <span>{label}</span>
                </button>
              ))}
            </div>
            {/* TODO_DROPPIN: real photo upload — POST /api/defect-photos
                with multipart/form-data, save returned URLs. */}
          </div>

          {/* v0.36.1 — photo viewer when a thumbnail is clicked. Shared
              component, used by PublicReportScreen too.
              2026-09-15 — Brett: "dont do full screen viewer for photos. just
              larger then what it was originally and zoom is ok." The contained
              viewer: a large centred panel over the card that zooms. */}
          {lightboxIdx !== null && photos.length > 0 && (
            <PhotoLightbox
              contained
              photos={photos}
              initialIdx={lightboxIdx}
              onClose={() => setLightboxIdx(null)}
              area={split ? overItemForm : null}
              markup={photoMarkup}
            />
          )}

          {/* v0.122.0 / v0.123.0 — Conversation (recipient questions, replies,
              comment photos + full-size viewer). v0.127.0 — extracted to the
              shared <DefectConversation>; renders nothing for a new pin or
              when the comments fetch fails (same gate as before). */}
          {/* v0.268.0 — focusConversation: opened from a client-reply email, so
              the card scrolls to what they wrote. */}
          {!split && (
            <DefectConversation
              defectId={defect?.id}
              scrollIntoViewOnLoad={focusConversation}
              composerRef={composerRef}
              onAddedToItem={onAddedToItem}
            />
          )}

          {errMsg && (
            <div style={{
              marginTop: 8, padding: '8px 10px',
              borderRadius: 6,
              background: theme.bg,
              border: `1px solid ${theme.danger}`,
              color: theme.danger,
              fontSize: 12, fontWeight: 600,
            }}>{errMsg}</div>
          )}
        </div>
        {split && (
          <div data-part="item-conversation" style={{
            width: 380, flexShrink: 0, minHeight: 0,
            borderLeft: `1px solid ${theme.border}`,
            background: theme.surface,
            padding: '14px 16px',
            display: 'flex', flexDirection: 'column',
          }}>
            <DefectConversation
              layout="panel"
              defectId={defect.id}
              composerRef={composerRef}
              onAddedToItem={onAddedToItem}
              photoArea={overItemForm}
            />
          </div>
        )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 16px',
          borderTop: `1px solid ${theme.border}`,
          background: theme.surface,
        }}>
          {onDelete && (
            <button
              onClick={onDelete}
              disabled={saving}
              style={{
                fontSize: 13, padding: '7px 12px',
                border: 0, background: 'transparent',
                color: theme.danger,
                cursor: saving ? 'default' : 'pointer',
                fontWeight: 600,
                fontFamily: 'inherit',
                opacity: saving ? 0.5 : 1,
              }}
            >Delete</button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button onClick={requestClose} disabled={saving} style={btnSecondary}>Cancel</button>
            <button
              onClick={submit}
              disabled={saving}
              style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
            >
              {saving ? 'Saving…' : (defect ? 'Save' : 'Create')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// v0.7.3 - segmented button group used for Priority + Status in the
// defect modal. Active button gets theme.brand background + white text.
// Optional swatch dot on the left (used for Priority colour-coding).
function Segmented({ options, value, onChange, withSwatch }) {
  return (
    <div
      role="group"
      style={{
        display: 'flex',
        border: `1px solid ${theme.border}`,
        borderRadius: 6,
        overflow: 'hidden',
        background: theme.surface,
      }}
    >
      {options.map((opt, i) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            style={{
              flex: 1,
              padding: '6px 4px',
              fontSize: 12,
              fontWeight: active ? 700 : 600,
              background: active ? theme.brand : 'transparent',
              color: active ? '#fff' : theme.text,
              border: 0,
              borderLeft: i === 0 ? 0 : `1px solid ${theme.border}`,
              cursor: 'pointer',
              fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 5,
              whiteSpace: 'nowrap',
            }}
          >
            {withSwatch && opt.swatch && (
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: opt.swatch,
                border: active ? '1px solid rgba(255,255,255,0.7)' : `1px solid ${theme.border}`,
                flexShrink: 0,
              }} />
            )}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// v0.287.0 - Previous / Next in the header. Small secondary buttons; at an end
// the button stays put, dimmed, and its tooltip says why.
function StepButton({ onClick, disabled, label, why, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={why || label}
      style={{
        ...btnSecondary,
        fontSize: 12,
        padding: '5px 10px',
        display: 'inline-flex', alignItems: 'center', gap: 4,
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function Label({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 600, color: theme.textLight,
      textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5,
    }}>{children}</div>
  );
}
